// Single source of truth for a Hunt's item domain: which item keys exist,
// how they're sorted, and how they're formatted/validated. Every view that
// used to hardcode a 1-100 range goes through here instead.

// The full ordered list of item keys for a fixed-domain hunt type, or null
// for 'custom' (no fixed universe — caller must derive keys from photos).
export function fixedDomain(huntType) {
  if (huntType === 'numbers') return Array.from({ length: 100 }, (_, i) => String(i + 1));
  if (huntType === 'alphabet') return Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
  return null;
}

// The list of item keys to actually render for a hunt, given its photos.
// Numbers/Alphabet: always the full fixed domain, all cells visible from
// creation. Custom: only items that have actually been photographed.
export function domainForHunt(hunt, huntPhotos) {
  const fixed = fixedDomain(hunt.type);
  if (fixed) return fixed;
  const seen = new Set(huntPhotos.map((p) => p.itemKey));
  return [...seen].sort(comparatorFor(hunt.type));
}

// Sort comparator for a hunt type's item keys.
export function comparatorFor(huntType) {
  return huntType === 'numbers' ? (a, b) => Number(a) - Number(b) : (a, b) => a.localeCompare(b);
}

// Cell/label display formatting — zero-padding only makes sense for Numbers.
export function formatLabel(huntType, itemKey) {
  return huntType === 'numbers' ? String(itemKey).padStart(2, '0') : itemKey;
}

// Implicit title for fixed-domain hunt types — Numbers/Alphabet hunts are
// never user-named. Custom hunts always carry a user-given title instead.
export function implicitTitle(huntType) {
  if (huntType === 'numbers') return '1-100';
  if (huntType === 'alphabet') return 'A-Z';
  return null;
}

// Validates a candidate item key against a hunt's domain rules.
// Numbers/Alphabet: must be a member of the fixed domain.
// Custom: any non-empty trimmed string is valid.
export function isValidItemKey(huntType, raw) {
  const fixed = fixedDomain(huntType);
  if (fixed) return fixed.includes(String(raw));
  return typeof raw === 'string' && raw.trim().length > 0;
}

// Case-insensitive match of a typed Custom item label against labels already
// used in this hunt, so "dog" and "Dog" collapse to one cell. Returns the
// existing canonical-cased label if found, otherwise null.
export function matchExistingLabel(raw, existingLabels) {
  const needle = raw.trim().toLowerCase();
  return existingLabels.find((label) => label.trim().toLowerCase() === needle) ?? null;
}
